import React from 'react';
import { Box, Slide, Typography, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDiagramContext } from '../Context/DiagramContext';
import DataTable from '../../../common/components/DataTable/DataTable';
import ProcessInfoSlide from '../common/ProcessInfoSlide';
import { updateProcessKPIs } from '../../../services/diagramApi';

// Local type for KPIs structure
interface KPIsData {
  [key: string]: { id: string; description: string };
}

const DiagramSlides: React.FC = () => {
  const {
    activeModal,
    setActiveModal,
    solutionData,
    setSelectedSolutionFromLibrary,
    taxonomySubprocessDescription,
    kpis,
    selectedCard,
  } = useDiagramContext();

  const handleUpdateKPIs = async (process_description: string, updatedKpis: KPIsData) => {
    try {
      const success = await updateProcessKPIs(
        selectedCard?.id ?? '',
        process_description,
        updatedKpis
      );
      return success;
    } catch (error) {
      console.error('KPI update failed:', error);
      return false;
    }
  };

  const isMobile = window.innerWidth <= 600;

  return (
    <>
      {/* AI Solutions Slide */}
      <Slide direction="left" in={activeModal === 'aiSolution'} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 0, sm: '15%' },
            right: 0,
            marginRight: { xs: 0, sm: '20px' },
            width: { xs: '100%', sm: 600 },
            maxWidth: { xs: '98%', md: '100%' },
            height: { xs: '100%', sm: '75vh' },
            bgcolor: 'background.paper',
            boxShadow: 4,
            p: { xs: 1, sm: 2 },
            zIndex: 1299,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            borderRadius: { xs: 0, sm: 1 },
            ...(isMobile && {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 9999,
              borderRadius: 0,
              margin: 0,
            }),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              ...(isMobile && {
                position: 'sticky',
                top: 0,
                backgroundColor: 'background.paper',
                zIndex: 1,
                borderBottom: '1px solid #e0e0e0',
                pb: 2,
                mb: 0,
              }),
            }}
          >
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              AI Opportunities
            </Typography>
            <IconButton size="small" onClick={() => setActiveModal(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          {solutionData.length > 0 ? (
            <DataTable
              columns={[
                {
                  id: 'opportunity_name',
                  label: 'AI Opportunities',
                  minWidth: 200,
                  sortable: true,
                  clickable: true,
                },
                {
                  id: 'impact',
                  label: 'Impact',
                  minWidth: 150,
                  align: 'center',
                  sortable: true,
                  clickable: true,
                },
                {
                  id: 'actions',
                  label: 'Actions',
                  minWidth: 50,
                  align: 'center',
                  actions: { view: true },
                  clickable: true,
                },
              ]}
              height="calc(100vh - 300px)"
              showRowBorders={true}
              rows={solutionData}
              onClick={(data: any) => {
                setSelectedSolutionFromLibrary({
                  id: data.id,
                  name: data.opportunity_name,
                  vault: data.vault,
                });
              }}
              onView={(data: any) => {
                setSelectedSolutionFromLibrary({
                  id: data.id,
                  name: data.opportunity_name,
                  vault: data.vault,
                });
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                ...(isMobile && {
                  flex: 1,
                  alignItems: 'center',
                  mt: 0,
                }),
              }}
            >
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} /> Loading...
              </Typography>
            </Box>
          )}
        </Box>
      </Slide>

      {/* Process Info Slide */}
      <ProcessInfoSlide
        open={activeModal === 'processInfo'}
        onClose={() => setActiveModal(null)}
        processDescription={taxonomySubprocessDescription || ''}
        kpis={(kpis || {}) as KPIsData}
        onUpdate={(description, updatedKpis) => {
          handleUpdateKPIs(description, updatedKpis);
        }}
      />
    </>
  );
};

export default DiagramSlides;
